// features/orders/OrdersPage.jsx
import React, { useState, useMemo, useCallback } from 'react'
import {
  ShoppingCart, TrendingUp, AlertCircle, CheckCircle,
  Plus, Download, Search, SlidersHorizontal,
} from 'lucide-react'
import { SplitViewLayout } from '../../components/layouts/SplitViewLayout'
import { OrdersList } from './components/OrdersList'
import { OrderDetail } from './components/OrderDetail'
import { QuickSaleModal } from './components/QuickSaleModal'
import { useOrders } from './hooks/useOrders'
import { formatMoney, resolvePeriodFilter } from '../../lib/format'
import { apiFetchBlob, downloadBlob } from '../../api'

function cx(...classes) {
  return classes.filter(Boolean).join(' ')
}

function KpiCard({ icon: Icon, label, value, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-slate-900',
    green: 'text-emerald-600',
    orange: 'text-amber-600',
    purple: 'text-violet-600',
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className={cx('mt-1 text-xl font-bold', tones[tone] || tones.neutral)}>
            {value}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
          <Icon className="h-5 w-5 text-slate-700" />
        </div>
      </div>
    </div>
  )
}

function toYYYYMMDD(d) {
  return d.toISOString().slice(0, 10)
}

function buildQueryString(params) {
  const qs = new URLSearchParams()
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return
    const s = String(v).trim()
    if (!s) return
    qs.set(k, s)
  })
  return qs.toString()
}

export function OrdersPage() {
  const [filters, setFilters] = useState({})
  const [quickSaleOpen, setQuickSaleOpen] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  const { orders, loading, error, refetch } = useOrders(filters)

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const exportWithType = useCallback(async (type) => {
    try {
      const qs = buildQueryString(filters)
      const endpoint = type === 'xlsx' ? '/api/orders/export.xlsx' : '/api/orders/export.csv'
      const blob = await apiFetchBlob(`${endpoint}?${qs}`)
      if (!blob || blob.size === 0) { window.alert('Aucune donnée pour cet export.'); return }
      downloadBlob(blob, `export_ventes_${toYYYYMMDD(new Date())}.${type}`)
    } catch (err) {
      window.alert(`Erreur export : ${err?.message || String(err)}`)
    }
  }, [filters])

  const kpis = useMemo(() => {
    const total = orders.length
    const unpaid = orders.filter(o => ['credit', 'installment'].includes(o.payment_mode)).length
    const paid = orders.filter(o => ['cash', 'paid'].includes(o.payment_mode)).length
    const revenue = orders.reduce((sum, o) => sum + (o.total_ttc || 0), 0)
    return { total, unpaid, paid, revenue }
  }, [orders])

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Historique des ventes</h1>
            <p className="text-xs text-slate-500 mt-0.5">Ventes réalisées et encaissements</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(v => !v)}
              className={cx(
                'flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-colors',
                showFilters
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              )}
            >
              <SlidersHorizontal size={13} />
              Filtres
            </button>
            <button
              type="button"
              onClick={() => exportWithType('csv')}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Download size={13} />
              CSV
            </button>
            <button
              type="button"
              onClick={() => exportWithType('xlsx')}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Download size={13} />
              Excel
            </button>
            <button
              type="button"
              onClick={() => setQuickSaleOpen(true)}
              className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition-colors"
            >
              <Plus size={13} />
              Nouvelle vente
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <KpiCard label="Total ventes" value={loading ? '—' : kpis.total} icon={ShoppingCart} />
          <KpiCard label="Impayées" value={loading ? '—' : kpis.unpaid} icon={AlertCircle} tone="orange" />
          <KpiCard label="Payées" value={loading ? '—' : kpis.paid} icon={CheckCircle} tone="green" />
          <KpiCard label="Chiffre d'affaires" value={loading ? '—' : formatMoney(kpis.revenue)} icon={TrendingUp} tone="purple" />
        </div>

        {/* Filtres */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2 pt-1">
            <div className="relative xl:col-span-2">
              <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Client, téléphone, référence…"
                className="w-full rounded-xl border border-slate-200 bg-white pl-8 pr-3 py-2 text-xs text-slate-900 outline-none focus:ring-2 focus:ring-slate-200"
              />
            </div>
            <input
              type="date"
              value={filters.from || ''}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:ring-2 focus:ring-slate-200"
            />
            <input
              type="date"
              value={filters.to || ''}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:ring-2 focus:ring-slate-200"
            />
            <select
              value={filters.payment_status || ''}
              onChange={(e) => handleFilterChange('payment_status', e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:ring-2 focus:ring-slate-200"
            >
              <option value="">Tous statuts</option>
              <option value="cash">Payées cash</option>
              <option value="pending">Crédit (aucun versement)</option>
              <option value="partial">Partiellement payées</option>
              <option value="late">En retard</option>
            </select>
          </div>
        )}

        {error && (
          <div className="rounded-xl bg-rose-50 border border-rose-100 px-4 py-2 text-xs text-rose-700">
            {error}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <SplitViewLayout
          basePath="/orders"
          listComponent={(props) => (
            <OrdersList
              {...props}
              orders={orders}
              loading={loading}
            />
          )}
          detailComponent={OrderDetail}
          detailProps={{ onDeleted: refetch, onUpdated: refetch }}
          emptyState={
            <div className="text-center text-slate-400 py-12">
              <ShoppingCart className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <div className="text-sm font-medium text-slate-500">Sélectionnez une vente</div>
              <div className="text-xs mt-1">Cliquez sur une ligne pour voir les détails</div>
            </div>
          }
        />
      </div>

      <QuickSaleModal
        open={quickSaleOpen}
        onClose={() => setQuickSaleOpen(false)}
        onSuccess={refetch}
      />
    </div>
  )
}
